/**
 * CmmdRealtimeClient — M2 (CMMD WebSocket subscription).
 *
 * Subscribes to CMMD's WebSocket endpoint for live event delivery.
 * Events are dispatched to registered handlers for sidebar reactivity.
 *
 * Auth: PAT is passed as a query param (`?wsToken=<pat>`) — matching
 *       the existing pattern used by Forge's own WS token auth.
 *
 * Reconnection: exponential backoff starting at 1s, capped at 30s.
 *               Stops immediately on intentional disconnect().
 *
 * Heartbeat: a ping is sent every HEARTBEAT_INTERVAL_MS; if no pong
 *            arrives within HEARTBEAT_TIMEOUT_MS the socket is closed
 *            and a reconnect is scheduled.
 *
 * Zero console.log/error/warn — errors surface through typed throws or
 * through toastManager (for user-visible connectivity issues).
 */

import { captureWebError } from "../sentry";

// ── Event types ───────────────────────────────────────────────────────────────

export type CmmdEvent =
  // ── Existing events (CMMD already emits these) ────────────────────────────
  | { readonly type: "TASK_ASSIGNED"; readonly taskId: string; readonly assigneeId: string }
  | { readonly type: "TASK_COMPLETED"; readonly taskId: string }
  | { readonly type: "TASK_COMMENTED"; readonly taskId: string; readonly commentId: string }
  | {
      readonly type: "COMMENT_CREATED";
      readonly entityType: string;
      readonly entityId: string;
      readonly commentId: string;
    }
  | { readonly type: "PROJECT_UPDATED"; readonly projectId: string }
  | { readonly type: "PROJECT_CREATED"; readonly projectId: string }
  | { readonly type: "BRAIN_DOCUMENT_CREATED"; readonly documentId: string }
  // ── Pending events (CMMD issue #80 — not yet emitted by server) ───────────
  | {
      readonly type: "TASK_UPDATED";
      readonly taskId: string;
      readonly changes: Partial<{
        status: string;
        priority: string;
        title: string;
        description: string;
      }>;
    }
  | { readonly type: "TASK_CREATED"; readonly taskId: string; readonly projectId: string | null }
  | {
      readonly type: "TASK_STATUS_CHANGED";
      readonly taskId: string;
      readonly fromStatus: string;
      readonly toStatus: string;
    }
  | {
      readonly type: "TASK_DESCRIPTION_CHANGED";
      readonly taskId: string;
      readonly description: string;
    }
  | {
      readonly type: "TASK_ATTACHMENT_ADDED";
      readonly taskId: string;
      readonly attachmentId: string;
    }
  | { readonly type: "TASK_DELETED"; readonly taskId: string }
  | {
      readonly type: "COMMENT_EDITED";
      readonly commentId: string;
      readonly entityId: string;
      readonly content: string;
    }
  | { readonly type: "PROJECT_MEMBER_ADDED"; readonly projectId: string; readonly userId: string };

const KNOWN_EVENT_TYPES = new Set<string>([
  // Existing
  "TASK_ASSIGNED",
  "TASK_COMPLETED",
  "TASK_COMMENTED",
  "COMMENT_CREATED",
  "PROJECT_UPDATED",
  "PROJECT_CREATED",
  "BRAIN_DOCUMENT_CREATED",
  // Pending (issue #80)
  "TASK_UPDATED",
  "TASK_CREATED",
  "TASK_STATUS_CHANGED",
  "TASK_DESCRIPTION_CHANGED",
  "TASK_ATTACHMENT_ADDED",
  "TASK_DELETED",
  "COMMENT_EDITED",
  "PROJECT_MEMBER_ADDED",
]);

// ── Config ────────────────────────────────────────────────────────────────────

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 25_000;
const HEARTBEAT_TIMEOUT_MS = 10_000;

// ── Public interface ──────────────────────────────────────────────────────────

export type CmmdEventHandler = (event: CmmdEvent) => void;
export type Unsubscribe = () => void;

export interface CmmdRealtimeClientInterface {
  connect(wsUrl: string, pat: string): void;
  disconnect(): void;
  /** Register a handler for CMMD events. Returns an unsubscribe function. */
  onEvent(handler: CmmdEventHandler): Unsubscribe;
}

// ── Implementation ────────────────────────────────────────────────────────────

class CmmdRealtimeClient implements CmmdRealtimeClientInterface {
  private ws: WebSocket | null = null;
  private wsUrl: string | null = null;
  private pat: string | null = null;
  private intentionallyClosed = false;
  private reconnectAttempt = 0;
  private reconnectTimerId: ReturnType<typeof setTimeout> | null = null;
  private heartbeatIntervalId: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly handlers = new Set<CmmdEventHandler>();

  connect(wsUrl: string, pat: string): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return; // already connected
    }
    this.wsUrl = wsUrl;
    this.pat = pat;
    this.intentionallyClosed = false;
    this.openSocket();
  }

  disconnect(): void {
    this.intentionallyClosed = true;
    this.clearReconnectTimer();
    this.clearHeartbeat();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  onEvent(handler: CmmdEventHandler): Unsubscribe {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  // ── Private — socket lifecycle ──────────────────────────────────────────────

  private openSocket(): void {
    if (!this.wsUrl || !this.pat) return;

    const url = buildWsUrl(this.wsUrl, this.pat);
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.addEventListener("open", this.handleOpen);
    ws.addEventListener("message", this.handleMessage);
    ws.addEventListener("close", this.handleClose);
    ws.addEventListener("error", this.handleError);
  }

  private readonly handleOpen = (): void => {
    this.reconnectAttempt = 0;
    this.startHeartbeat();
  };

  private readonly handleMessage = (event: MessageEvent): void => {
    this.resetHeartbeatTimeout();

    // Handle pong
    if (event.data === "pong") return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(event.data as string);
    } catch {
      return; // malformed JSON — silently ignore
    }

    const cmmdEvent = parseCmmdEvent(parsed);
    if (!cmmdEvent) return; // unknown event type — silently ignore

    for (const handler of this.handlers) {
      handler(cmmdEvent);
    }
  };

  private readonly handleClose = (event: CloseEvent): void => {
    this.clearHeartbeat();
    if (this.intentionallyClosed) return;
    if (event.code === 1000) return; // clean close — no reconnect

    // Codes 4000+ are application-level rejections (auth, rate limit, etc.) and
    // are worth reporting. Standard 1xxx codes are transient network flaps.
    if (event.code >= 4000) {
      captureWebError(new Error(`CMMD realtime closed: ${event.code} ${event.reason}`), {
        route: "cmmd-realtime",
        ws_close_code: String(event.code),
      });
    }

    this.scheduleReconnect();
  };

  private readonly handleError = (_event: Event): void => {
    // Error always precedes a close event — reconnect is handled there
  };

  private scheduleReconnect(): void {
    if (this.intentionallyClosed) return;
    const delay = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempt),
      RECONNECT_CAP_MS,
    );
    this.reconnectAttempt++;
    this.reconnectTimerId = setTimeout(() => {
      if (!this.intentionallyClosed) {
        this.openSocket();
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
  }

  // ── Private — heartbeat ─────────────────────────────────────────────────────

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.heartbeatIntervalId = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send("ping");
        this.heartbeatTimeoutId = setTimeout(() => {
          // No pong received — connection is stale; close + reconnect
          this.ws?.close();
        }, HEARTBEAT_TIMEOUT_MS);
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private resetHeartbeatTimeout(): void {
    if (this.heartbeatTimeoutId !== null) {
      clearTimeout(this.heartbeatTimeoutId);
      this.heartbeatTimeoutId = null;
    }
  }

  private clearHeartbeat(): void {
    if (this.heartbeatIntervalId !== null) {
      clearInterval(this.heartbeatIntervalId);
      this.heartbeatIntervalId = null;
    }
    this.resetHeartbeatTimeout();
  }
}

// ── Factory ───────────────────────────────────────────────────────────────────

export function createCmmdRealtimeClient(): CmmdRealtimeClientInterface {
  return new CmmdRealtimeClient();
}

/**
 * Shared singleton for use in the web app.
 * Tests should create their own via `createCmmdRealtimeClient()`.
 */
export const cmmdRealtimeClient: CmmdRealtimeClientInterface = new CmmdRealtimeClient();

// ── Private helpers ───────────────────────────────────────────────────────────

function buildWsUrl(wsUrl: string, pat: string): string {
  const url = new URL(wsUrl);
  url.searchParams.set("wsToken", pat);
  return url.toString();
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function parseCmmdEvent(raw: unknown): CmmdEvent | null {
  if (!isObject(raw)) return null;
  const type = raw["type"];
  if (typeof type !== "string" || !KNOWN_EVENT_TYPES.has(type)) return null;

  switch (type) {
    case "TASK_ASSIGNED": {
      const taskId = raw["taskId"];
      const assigneeId = raw["assigneeId"];
      if (typeof taskId !== "string") return null;
      if (typeof assigneeId !== "string") return null;
      return { type, taskId, assigneeId };
    }
    case "TASK_COMPLETED": {
      const taskId = raw["taskId"];
      if (typeof taskId !== "string") return null;
      return { type, taskId };
    }
    case "TASK_COMMENTED": {
      const taskId = raw["taskId"];
      const commentId = raw["commentId"];
      if (typeof taskId !== "string" || typeof commentId !== "string") return null;
      return { type, taskId, commentId };
    }
    case "COMMENT_CREATED": {
      const entityType = raw["entityType"];
      const entityId = raw["entityId"];
      const commentId = raw["commentId"];
      if (
        typeof entityType !== "string" ||
        typeof entityId !== "string" ||
        typeof commentId !== "string"
      )
        return null;
      return { type, entityType, entityId, commentId };
    }
    case "PROJECT_UPDATED": {
      const projectId = raw["projectId"];
      if (typeof projectId !== "string") return null;
      return { type, projectId };
    }
    case "PROJECT_CREATED": {
      const projectId = raw["projectId"];
      if (typeof projectId !== "string") return null;
      return { type, projectId };
    }
    case "BRAIN_DOCUMENT_CREATED": {
      const documentId = raw["documentId"];
      if (typeof documentId !== "string") return null;
      return { type, documentId };
    }
    // ── Pending events (issue #80) ──────────────────────────────────────────
    case "TASK_UPDATED": {
      const taskId = raw["taskId"];
      if (typeof taskId !== "string") return null;
      const changes = isObject(raw["changes"]) ? raw["changes"] : {};
      return {
        type,
        taskId,
        changes: {
          ...(typeof changes["status"] === "string" ? { status: changes["status"] } : {}),
          ...(typeof changes["priority"] === "string" ? { priority: changes["priority"] } : {}),
          ...(typeof changes["title"] === "string" ? { title: changes["title"] } : {}),
          ...(typeof changes["description"] === "string"
            ? { description: changes["description"] }
            : {}),
        },
      };
    }
    case "TASK_CREATED": {
      const taskId = raw["taskId"];
      if (typeof taskId !== "string") return null;
      const projectId = typeof raw["projectId"] === "string" ? raw["projectId"] : null;
      return { type, taskId, projectId };
    }
    case "TASK_STATUS_CHANGED": {
      const taskId = raw["taskId"];
      const fromStatus = raw["fromStatus"];
      const toStatus = raw["toStatus"];
      if (
        typeof taskId !== "string" ||
        typeof fromStatus !== "string" ||
        typeof toStatus !== "string"
      )
        return null;
      return { type, taskId, fromStatus, toStatus };
    }
    case "TASK_DESCRIPTION_CHANGED": {
      const taskId = raw["taskId"];
      const description = raw["description"];
      if (typeof taskId !== "string" || typeof description !== "string") return null;
      return { type, taskId, description };
    }
    case "TASK_ATTACHMENT_ADDED": {
      const taskId = raw["taskId"];
      const attachmentId = raw["attachmentId"];
      if (typeof taskId !== "string" || typeof attachmentId !== "string") return null;
      return { type, taskId, attachmentId };
    }
    case "TASK_DELETED": {
      const taskId = raw["taskId"];
      if (typeof taskId !== "string") return null;
      return { type, taskId };
    }
    case "COMMENT_EDITED": {
      const commentId = raw["commentId"];
      const entityId = raw["entityId"];
      const content = raw["content"];
      if (
        typeof commentId !== "string" ||
        typeof entityId !== "string" ||
        typeof content !== "string"
      )
        return null;
      return { type, commentId, entityId, content };
    }
    case "PROJECT_MEMBER_ADDED": {
      const projectId = raw["projectId"];
      const userId = raw["userId"];
      if (typeof projectId !== "string" || typeof userId !== "string") return null;
      return { type, projectId, userId };
    }
    default:
      return null;
  }
}
